import { useState, ChangeEvent, FormEvent } from 'react';
import Image from 'next/image';

import colours from '@common/colours';
import { BlueSubmitButton } from '@common/buttons';
import { BodyText, H3 } from '@common/textElements';

import { postGuidesQuery } from '@nextUtils/guidesUtils';
import { ErrorTracking } from '@common/errorTracking';

import facebook from '@public/img/social-media-icons/facebook.png';
import twitter from '@public/img/social-media-icons/twitter.png';

const initialState = {
  name: '',
  email: '',
  comment: '',
};

type Props = {
  commentId?: number;
  postId: number;
  absoluteCanonicalURL: string;
};

const CommentResponseForm = ({
  commentId,
  postId,
  absoluteCanonicalURL,
}: Props): JSX.Element => {
  const [formData, setFormData] = useState(initialState);

  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [failureMessage, setFailureMessage] = useState('');

  const onChange = (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) =>
    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
    });

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmittingForm) {
      return;
    }

    setIsSubmittingForm(true);
    setShowSuccess(false);
    setFailureMessage('');

    try {
      const query = `
        mutation {
          createComment(input: {
            clientMutationId: "_",
            commentOn: ${postId},
            content: """${formData.comment}""",
            author: "${formData.name}",
            ${formData.email ? `authorEmail: "${formData.email}",` : ''}
            ${commentId !== undefined ? `parent: ${commentId},` : ''}
          }) {
            comment {
              approved
            }
          }
        }
      `;

      // Keep this request HTTPS (unlike other guides requests) as it has private data posted by the user
      const json = await postGuidesQuery(query);

      // NB response.ok is set to true even if there was e.g. a bruteforce or invalid email address error
      if (!json.errors) {
        setShowSuccess(true);
        setFormData(initialState);

        // Eventually here we could check json.data.createComment.comment.approved and if true then refresh
        // the comments
        // For now, just show a general message that all comments are to be moderated (even though some may
        // be automatically approved)
      } else {
        // The response we get back from graph QL is pretty clear in general and can be used directly, e.g.:
        // - The email address you are trying to use is invalid      (NB no full stop at the end)
        // - Duplicate comment detected; it looks as though you&#8217;ve already said that!
        // - You are posting comments too quickly. Slow down.

        const addFullStop =
          !json.errors[0].message.endsWith('.') &&
          !json.errors[0].message.endsWith('!');

        const message = json.errors[0].message.replace('&#8217;', "'");

        setFailureMessage(`Error: ${message}${addFullStop ? '.' : ''}`);
      }
    } catch (err) {
      ErrorTracking.track(err);

      if ((err as Error).message === 'Failed to fetch') {
        setFailureMessage(
          'There was an error submitting your comment - please check your internet connection and try again.',
        );
      } else {
        setFailureMessage('There was an error submitting your comment');
      }
    } finally {
      setIsSubmittingForm(false);
    }
  };

  return (
    <form onSubmit={onSubmit}>
      {commentId === undefined && <H3>Write a response</H3>}
      <input
        name="name"
        placeholder="Name"
        type="text"
        className="mt-[13px] w-full rounded p-[12px] font-bold text-blue"
        value={formData.name}
        onChange={onChange}
        required
      />
      <input
        name="email"
        placeholder="Email"
        type="text"
        className="mt-[13px] w-full rounded p-[12px] font-bold text-blue"
        value={formData.email}
        onChange={onChange}
      />
      <textarea
        name="comment"
        placeholder="Comment"
        rows={5}
        className="mt-[13px] w-full resize-none rounded p-[12px] font-bold text-blue"
        value={formData.comment}
        onChange={onChange}
        required
      />
      <div className="mt-[8px] flex justify-end">
        <BlueSubmitButton buttonType="submit">Post</BlueSubmitButton>
      </div>
      <div className="mb-[80px] text-right md:m-auto md:mb-[50px] md:w-[500px] md:text-center">
        {showSuccess && (
          <>
            <BodyText
              colour={colours.darkGreen}
              marginTop={10}
              textAlign="center"
              maxWidth={700}
            >
              Comment submitted successfully, thank you! <br /> Our moderators
              will review your comment in the next 1-2 days and it will appear
              on this article once it has been approved.
            </BodyText>
            <div className="mt-[25px] text-center text-blue">
              Share this{' '}
              {absoluteCanonicalURL.includes('/stories/') ? 'story' : 'article'}
              :{' '}
              <div className="mt-2 flex justify-center">
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${absoluteCanonicalURL}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Image
                    className="ml-[10px]"
                    src={facebook}
                    alt="Facebook"
                    width={30}
                    height={30}
                  />
                </a>
                <a
                  href={`https://twitter.com/share?url=${absoluteCanonicalURL}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Image
                    className="ml-[10px]"
                    src={twitter}
                    alt="Twitter"
                    width={30}
                    height={30}
                  />
                </a>
              </div>
            </div>
          </>
        )}
        {failureMessage && (
          <BodyText
            colour={colours.snOrange}
            marginTop={10}
            textAlign="center"
            maxWidth={700}
          >
            {failureMessage}
          </BodyText>
        )}
      </div>
    </form>
  );
};

export default CommentResponseForm;
